import React from 'react';
import { fmtS } from '../backbone.data';

export interface UsageBarProps {
    used: number;
    cap: number;
    height?: number;
    /** Optional: override bar fill color (e.g. "bg-[var(--color-semantic-bg-brand-bold)]") */
    barClassName?: string;
    /**
     * When `used > 0` and the real fill % is below this value, clamp to this % so a sliver is visible
     * (large cap vs tiny usage). Does not change `used`/`cap` semantics elsewhere.
     */
    minWidthPercentWhenPositive?: number;
}

export const UsageBar: React.FC<UsageBarProps> = ({
    used,
    cap,
    height = 8,
    barClassName,
    minWidthPercentWhenPositive,
}) => {
    const rawPct = cap > 0 ? Math.min((used / cap) * 100, 100) : 0;
    const pct =
        minWidthPercentWhenPositive != null &&
        used > 0 &&
        rawPct > 0 &&
        rawPct < minWidthPercentWhenPositive
            ? minWidthPercentWhenPositive
            : rawPct;

    const getBarColor = () => {
        if (barClassName) return barClassName;
        if (pct > 90)
            return 'bg-[var(--color-semantic-bg-danger-bold)]';
        if (pct > 70)
            return 'bg-[var(--color-semantic-bg-warning-bold)]';
        if (pct > 40)
            return 'bg-[var(--color-semantic-bg-brand-bold)]';
        return 'bg-[var(--color-semantic-bg-success-bold)]';
    };

    return (
        <div
            className="w-full rounded-md overflow-hidden bg-[var(--color-semantic-bg-tertiary)]"
            style={{ height }}
        >
            <div
                className={`h-full rounded-md transition-all duration-500 ease-out ${getBarColor()}`}
                style={{ width: `${pct}%` }}
            />
        </div>
    );
};

interface MiniChartProps {
    data: number[];
    color?: string;
    height?: number;
    width?: number;
}

export const MiniChart: React.FC<MiniChartProps> = ({
    data,
    color = 'var(--color-semantic-fg-brand-primary)',
    height = 32,
    width = 120,
}) => {
    if (!data || data.length < 2) return null;

    const max = Math.max(...data) * 1.1 || 1;
    const pts = data
        .map(
            (v, i) =>
                `${(i / (data.length - 1)) * width},${height - (v / max) * (height - 4)}`
        )
        .join(' ');

    return (
        <svg width={width} height={height} className="block">
            <polyline
                points={pts}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
            />
            <polyline
                points={`0,${height} ${pts} ${width},${height}`}
                fill={color}
                opacity={0.08}
            />
        </svg>
    );
};

interface GaugeRingProps {
    used: number;
    total: number;
    size?: number;
}

export const GaugeRing: React.FC<GaugeRingProps> = ({
    used,
    total,
    size = 130,
}) => {
    const pct = Math.min((used / total) * 100, 100);
    const r = (size - 12) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;

    const getColor = () => {
        if (pct > 90) return 'var(--color-primitive-red-500)';
        if (pct > 70) return 'var(--color-primitive-yellow-500)';
        if (pct > 40) return 'var(--color-primitive-brand-surf-500)';
        return 'var(--color-primitive-green-500)';
    };

    const fg = getColor();

    return (
        <svg width={size} height={size}>
            <circle
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke="var(--color-semantic-bg-tertiary)"
                strokeWidth={10}
            />
            <circle
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={fg}
                strokeWidth={10}
                strokeDasharray={circ}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform={`rotate(-90 ${cx} ${cy})`}
                className="transition-all duration-700 ease-out"
            />
            <text
                x={cx}
                y={cy - 4}
                textAnchor="middle"
                fontSize={20}
                fontWeight={800}
                fill={fg}
            >
                {fmtS(used)}
            </text>
            <text
                x={cx}
                y={cy + 14}
                textAnchor="middle"
                fontSize={10}
                fill="var(--color-semantic-text-secondary)"
            >
                of {fmtS(total)}
            </text>
        </svg>
    );
};

export { CounterComponent } from './CounterComponent';
